import { MeasurementSystem } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React from 'react';
import { Layer } from 'react-konva';
import {
  useRecoilState,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import LocationFeature from '../../../../layout/features/LocationFeature';
import { stageStateById } from '../../../../layout/stage/stage.state';
import { warehouseMeasurementSystem } from '../../../../store/warehouse.state';
import { ConvertedLocationFeature } from '../converter.serializable.model';
import {
  converterAreaConfiguration,
  converterEditableArea,
} from '../store/converter.area.state';
import { getLayerConfig } from '../store/converter.helper';
import {
  converterLayoutHoveredLocation,
  converterLayoutLocations,
  converterLayoutSelectedLocationId,
  converterLayoutShowLocations,
} from '../store/converter.layout.state';
import {
  converterMultiSelectedLocationsIds,
  converterMultiSelectLocationIdSetter,
  converterMultiSelectLocationsEnabled,
} from '../store/converter.state';
import useConverterLocationColor from './useConverterLocationColor';

const ConverterLocationLayer: React.FC = () => {
  const showLocations = useRecoilValue(converterLayoutShowLocations);
  const areaResponse = useRecoilValueLoadable(converterEditableArea);
  const areasConfigResponse = useRecoilValueLoadable(
    converterAreaConfiguration,
  );
  const [selectedId, setSelectedId] = useRecoilState(
    converterLayoutSelectedLocationId,
  );
  const multiSelectedLocationsIds = useRecoilValue(
    converterMultiSelectedLocationsIds,
  );
  const setMultiSelectedLocationsId = useSetRecoilState(
    converterMultiSelectLocationIdSetter,
  );
  const isMultiselectEnabled = useRecoilValue(
    converterMultiSelectLocationsEnabled,
  );

  const setHovered = useSetRecoilState(converterLayoutHoveredLocation);

  const locationsResponse = useRecoilValueLoadable(converterLayoutLocations);

  const stage = useRecoilValue(stageStateById('converter-area-view'));
  const ms = useRecoilValue(warehouseMeasurementSystem);

  // Get locations data or empty array if not yet loaded
  const locationsData =
    locationsResponse.state === 'hasValue' ? locationsResponse.contents : [];

  // Keep hook at the top level with safe fallback for locations
  const [getColor, getGradientColor] = useConverterLocationColor({
    locations: locationsData,
  });

  if (
    areasConfigResponse.state !== 'hasValue' ||
    locationsResponse.state !== 'hasValue' ||
    areaResponse.state !== 'hasValue' ||
    !areaResponse.contents ||
    !showLocations
  )
    return null;

  const area = areaResponse.contents;
  const locations = locationsResponse.contents;
  const areasConfig = areasConfigResponse.contents;

  if (_.isEmpty(locations)) {
    return null;
  }

  const areaConfig = areasConfig[area?.id];

  function getTitle(l: ConvertedLocationFeature) {
    const minSize = ms === MeasurementSystem.METRIC ? 50 : 20;
    return stage.scale * l.locationLength > minSize ? l.locationId : null;
  }
  function onClick(l: ConvertedLocationFeature, withShift: boolean) {
    if (isMultiselectEnabled && withShift) {
      setMultiSelectedLocationsId(l.locationId);
    } else {
      setSelectedId(l.locationId === selectedId ? null : l.locationId);
    }
  }

  return (
    <Layer {...getLayerConfig(areaConfig, area)}>
      {locations.map(l => {
        // Use explicit color values with forced type to ensure proper rendering
        let locationColor = getColor ? getColor(l) : null;

        if (_.has(multiSelectedLocationsIds, l.locationId)) {
          locationColor = ['red', null];
        }

        // Ensure color is properly formatted as [string, string]
        if (!locationColor || !Array.isArray(locationColor)) {
          // Default blue/green colors based on location ID (even/odd)
          const isEven =
            parseInt(l.locationId.replace(/[^0-9]/g, ''), 10) % 2 === 0;
          locationColor = isEven ? ['#80FF', null] : ['#00CC00', null];
        }

        // console.log('Location color for', l.locationId, ':', locationColor);

        return (
          <LocationFeature
            key={l.locationId}
            loc={l}
            title={getTitle(l)}
            onClick={withShift => onClick(l, withShift)}
            color={locationColor}
            onMouseOver={() => {
              setHovered(l);
            }}
            onMouseOut={() => {
              setHovered(null);
            }}
          />
        );
      })}
    </Layer>
  );
};

export default ConverterLocationLayer;
